import { useContext, useMemo } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ErrorIcon from '@assets/images/attention.svg';
import { UserContext } from '@contexts/user-context';
import { Back } from '@pulse-web-ui/icons';
import { Spinner } from '@pulse-web-ui/spinner';
import { useTheme } from '@pulse-web-ui/theme';
import { observer } from 'mobx-react-lite';

import { sendAnalyticEvent } from '@app/web-analytic';

import { useStores } from '@shared/hooks';
import {
  AnalyticEvent,
  MAIN,
  RowContainer,
  SRG_LINK,
  StateBlock,
  TextLink,
  WidgetSubHeaderText,
  getSupportMailDataValues,
} from '@shared/index';

import { useSrgAccess } from '../api';
import { IconContainer } from './access-to-srg.styles';

export const AccessToSRG = observer(() => {
  const { t } = useTranslation('ACCESS_TO_SRG');
  const { getUserName } = useContext(UserContext);
  const {
    MainStore: {
      agentContractStore: { profile },
      agentUserStore: { agent },
    },
  } = useStores();
  const navigate = useNavigate();
  const theme = useTheme();
  const username = getUserName();

  const values = useMemo(
    () =>
      getSupportMailDataValues({
        t,
        username,
        profile,
        agent,
        appealPlace: window.location.href,
      }),
    [t, username, profile, agent, window.location.href]
  );

  const { isFetching, refetch } = useSrgAccess(
    {
      isSrgLoginObtained: true,
    },
    []
  );

  const goToSrg = () => {
    sendAnalyticEvent(AnalyticEvent.AGENT_MORTGAGE_THEREACCESS);
    refetch();
    goToMain();
    window.open(SRG_LINK, '_blank');
  };

  const goToMain = () => {
    sendAnalyticEvent(AnalyticEvent.AGENT_MORTGAGE_CLICKOK);
    navigate(MAIN);
  };

  return (
    <StateBlock
      title={t('headers.accessNeeded')}
      icon={ErrorIcon}
      content={
        <WidgetSubHeaderText $center>
          <Trans
            components={{ supportLink: <TextLink href={values.mailtoHref} /> }}
          >
            {t('subHeaders.description') + t('COMMON:mailto.body')}
          </Trans>
        </WidgetSubHeaderText>
      }
      cancelButtonText={
        isFetching ? (
          <Spinner />
        ) : (
          <RowContainer
            $gap={2}
            $marginBottom={0}
          >
            {t('buttons.goToSRG')}{' '}
            <IconContainer>
              <Back
                width={16}
                color={theme.colors.icon.primary}
              />
            </IconContainer>
          </RowContainer>
        )
      }
      submitButtonText={t('buttons.goToMain')}
      onSubmit={goToMain}
      onCancel={goToSrg}
    />
  );
});
